import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";

import { isDesktopMediaQuery } from "../../../constants/constants";
import { fetchSocialPostsThunk } from "../../../redux-stores/slices/influencerContentSlice";
import { setSelectedSocialPost } from "../../../redux-stores/slices/layoutSlice";
import { AppDispatch, RootState } from "../../../redux-stores/store";
import { MediaProductType, SocialAccountData } from "../../../types/influencer/influencerTypes";

import "./style.scss";
import SocialPostCardV2 from "./SocialPostCardV2";

type TabKey = MediaProductType;

type TabConfig = {
    key: TabKey;
    label: string;
    supportedPlatforms: Array<SocialAccountData["social_account"]["platform"]>;
};

const TABS: TabConfig[] = [
    { key: MediaProductType.REEL, label: "Reels", supportedPlatforms: ["ig"] },
    { key: MediaProductType.FEED, label: "Post", supportedPlatforms: ["ig"] },
    // { key: MediaProductType.STORY, label: "Stories", supportedPlatforms: ["ig"] },
    { key: MediaProductType.VIDEO, label: "All Videos", supportedPlatforms: ["yt"] },
];

const InfluencerSocialContentV2: React.FC<{ socialAccount: SocialAccountData }> = ({ socialAccount }) => {
    const platform = socialAccount?.social_account.platform;
    const dispatch: AppDispatch = useDispatch();

    const availableTabs = useMemo(
        () => TABS.filter(t => t.supportedPlatforms.includes(platform)),
        [platform]
    );

    const [mediaProductType, setMediaProductType] = useState<MediaProductType | null>(null);

    useEffect(() => {
        if (!platform) return;
        if (platform === "ig") setMediaProductType(MediaProductType.REEL);
        if (platform === "yt") setMediaProductType(MediaProductType.VIDEO);
    }, [platform]);

    const data = useSelector((state: RootState) => {
        if (!socialAccount?.social_account.uuid || !mediaProductType) return undefined;
        return state.influencerContent.social_posts?.[socialAccount.social_account.uuid]?.[mediaProductType];
    });

    const posts = data ? data.items : [];
    const page = data ? data.page : 1;
    const hasMore = data ? data.has_more : true;
    const loading = useSelector((state: RootState) => state.influencerContent.loading);

    const fetchContent = useCallback(
        async (pageNumber: number) => {
            if (!socialAccount || !mediaProductType) return;
            dispatch(
                fetchSocialPostsThunk({
                    socialAccountId: socialAccount.social_account.uuid,
                    mediaProductType,
                    page: pageNumber,
                })
            );
        },
        [dispatch, mediaProductType, socialAccount]
    );

    useEffect(() => {
        if (!mediaProductType) return;
        fetchContent(1);
    }, [mediaProductType, fetchContent, socialAccount]);

    const nextPage = () => {
        if (hasMore && !loading) fetchContent(page + 1);
    };

    const tabsRootRef = useRef<HTMLDivElement | null>(null);
    const tabButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
    const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);

    useLayoutEffect(() => {
        if (!mediaProductType) return;
        const rootEl = tabsRootRef.current;
        const btn = tabButtonRefs.current[String(mediaProductType)];
        if (!rootEl || !btn) return;

        const rootRect = rootEl.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        const left = btnRect.left - rootRect.left;
        const width = btnRect.width;

        setIndicatorStyle({ left, width });
    }, [mediaProductType, availableTabs.length]);

    return (
        <div className="influencer-social-content-v2 app-card p-0">
            <div className="influencer-social-content-v2__header">
                <p className="influencer-social-content-v2__title m-0">Content</p>
            </div>

            <div className="influencer-social-content-v2__tabs" ref={tabsRootRef}>
                <div className="influencer-social-content-v2__tabs-row">
                    {availableTabs.map(tab => {
                        const active = tab.key === mediaProductType;
                        return (
                            <button
                                key={tab.key}
                                ref={el => {
                                    tabButtonRefs.current[String(tab.key)] = el;
                                }}
                                type="button"
                                className={`influencer-social-content-v2__tab ${active ? "is-active" : ""}`}
                                onClick={() => setMediaProductType(tab.key)}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="influencer-social-content-v2__tabs-divider border-bottom-1" />
                {indicatorStyle && (
                    <div
                        className="influencer-social-content-v2__tabs-indicator"
                        style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
                    />
                )}
            </div>

            <div className="influencer-social-content-v2__body">
                <InfiniteScroll
                    className="influencer-social-content-v2__grid"
                    hasMore={hasMore}
                    next={nextPage}
                    dataLength={hasMore ? posts.length + 1 : posts.length}
                    loader={<></>}
                    scrollThreshold="200px"
                >
                    {posts.map((post, idx) => (
                        <div className="influencer-social-content-v2__grid-item" key={post.uuid ?? idx}>
                            <SocialPostCardV2
                                post={post}
                                onPostClick={postId =>
                                    dispatch(
                                        setSelectedSocialPost({
                                            socialPostId: postId,
                                            socialAccountId: socialAccount?.social_account.uuid!,
                                        })
                                    )
                                }
                            />
                        </div>
                    ))}

                    {loading &&
                        Array.from({ length: 9 }).map((_, i) => (
                            <div className="influencer-social-content-v2__grid-item" key={`sk-${i}`}>
                                <Skeleton className="influencer-social-content-v2__skeleton" />
                            </div>
                        ))}
                </InfiniteScroll>

                {!loading && !posts.length && (
                    <div className="influencer-social-content-v2__empty text-muted fs-14">
                        No content yet!
                    </div>
                )}
            </div>
        </div>
    );
};

export default InfluencerSocialContentV2;

